'use client';

import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@workspace/lib';
import { Upload, X, FileText } from 'lucide-react';
import { theme } from '../../theme';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface FileInputProps {
  /** Currently selected File, or null when empty. Controlled. */
  value?: File | null;
  /** Called with the newly selected File (or null when cleared). */
  onChange?: (file: File | null) => void;
  onBlur?: () => void;
  /** HTML accept attribute, e.g. ".jpg,.jpeg,.png,.pdf" */
  accept?: string;
  /** Max file size in MB — triggers onSizeError when exceeded. */
  maxSizeMB?: number;
  status?: 'default' | 'error' | 'success';
  isDisabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /** Called when the selected file exceeds maxSizeMB. */
  onSizeError?: (message: string) => void;
  /** Called when the selected file has an unsupported MIME type. */
  onTypeError?: (message: string) => void;
  /** Whitelist of MIME types for client-side validation. */
  acceptedMime?: string[];
  /** Helper text shown below the input (e.g. size/type constraints). */
  hint?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionsFromAccept(accept: string): string[] {
  return accept
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .map((part) => (part.startsWith('.') ? part.slice(1) : part))
    .filter(Boolean);
}

/** MIME is unreliable for HEIC/HEIF (often empty, `image/heif`, or octet-stream on Windows). */
function isAllowedFile(file: File, accept: string, acceptedMime: string[] | undefined): boolean {
  if (!acceptedMime?.length) return true;
  if (file.type && acceptedMime.includes(file.type)) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ext) return false;
  return extensionsFromAccept(accept).includes(ext);
}

// ── CVA ───────────────────────────────────────────────────────────────────────

const dropZoneCva = cva(
  'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-6 transition-colors duration-200',
  {
    variants: {
      status: {
        default: 'border-secondary-200 bg-secondary-50/50 hover:border-primary-300 hover:bg-primary-50/30',
        error: 'border-error bg-error-50/30',
        success: 'border-success bg-success-50/30',
      },
      isDragOver: {
        true: 'border-primary-400 bg-primary-50/50',
        false: '',
      },
      isDisabled: {
        true: 'cursor-not-allowed opacity-60',
        false: '',
      },
    },
  },
);

const fileCardCva = cva(
  'relative flex cursor-pointer items-center gap-3 rounded-md border bg-white px-4 py-3 transition-colors duration-200 hover:bg-secondary-50/50',
  {
    variants: {
      status: {
        default: 'border-secondary-200',
        error: 'border-error',
        success: 'border-success',
      },
      isDisabled: {
        true: 'cursor-not-allowed opacity-60',
        false: '',
      },
    },
  },
);

const uploadIconBgCva = cva('flex h-10 w-10 items-center justify-center rounded-full', {
  variants: {
    site: {
      porta: 'bg-secondary-100',
      portaNZ: 'bg-secondary-100',
      redRec: 'bg-primary-200',
      easycraft: 'bg-secondary-100',
    },
    isDragOver: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [{ isDragOver: true, className: 'bg-primary-100' }],
});

// ── FileInput ─────────────────────────────────────────────────────────────────

/**
 * `FileInput` — a modern file upload component.
 *
 * Shows a drop-zone style upload area when empty, and a file preview card with
 * an inline X remove button when a file is selected. The "Choose file" and
 * "Remove" actions are vertically stacked/aligned within the component.
 *
 * Designed to be composed inside a react-hook-form `<Controller>`.
 */
export function FileInput({
  value,
  onChange,
  onBlur,
  accept = '.jpg,.jpeg,.png,.heic,.heif,.webp,.pdf',
  maxSizeMB = 10,
  status = 'default',
  isDisabled = false,
  className,
  id,
  name,
  onSizeError,
  onTypeError,
  acceptedMime,
  hint,
}: FileInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const pickingFileRef = React.useRef(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    // Reset so re-selecting the same file fires onChange again
    e.target.value = '';

    if (!file) {
      // User cancelled the dialog — don't fire onChange(null) which
      // would trigger "required" validation.
      return;
    }

    if (!isAllowedFile(file, accept, acceptedMime)) {
      onTypeError?.('Please upload an image (JPEG, PNG, HEIC, WebP).');
      // Don't call onChange(null) — let the error callback handle messaging
      // without triggering form-level "required" validation.
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      onSizeError?.(`File must be under ${maxSizeMB} MB. Try uploading a smaller sized photo.`);
      return;
    }

    onChange?.(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isDisabled) return;

    const file = e.dataTransfer.files?.[0] ?? null;
    if (!file) return;

    if (!isAllowedFile(file, accept, acceptedMime)) {
      onTypeError?.('Please upload an image (JPEG, PNG, HEIC, WebP).');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      onSizeError?.(`File must be under ${maxSizeMB} MB. Try uploading a smaller sized photo.`);
      return;
    }

    onChange?.(file);
  };

  const handleRemove = () => {
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  /** Open the native file picker, suppressing blur while it's open. */
  const openFilePicker = () => {
    if (isDisabled) return;
    pickingFileRef.current = true;
    inputRef.current?.click();
    // The dialog is modal — once it closes the input regains focus briefly,
    // then blurs. We use a short timeout to re-enable blur handling.
    setTimeout(() => {
      pickingFileRef.current = false;
    }, 500);
  };

  /* ── Hidden native file input ──────────────────────────────────────────── */

  const hiddenInput = (
    <input
      ref={inputRef}
      id={id}
      name={name}
      type="file"
      accept={accept}
      disabled={isDisabled}
      onBlur={() => {
        // Suppress blur while the native file dialog is open to prevent
        // premature "required" validation.
        if (!pickingFileRef.current) {
          onBlur?.();
        }
      }}
      onChange={handleChange}
      className="sr-only"
      tabIndex={-1}
    />
  );

  /* ── File selected state ───────────────────────────────────────────────── */

  if (value) {
    return (
      <div className={cn('grid w-full gap-1.5', className)}>
        {hiddenInput}

        <div onClick={openFilePicker} className={fileCardCva({ status, isDisabled })}>
          {/* File icon */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50">
            <FileText className="h-5 w-5 text-primary-500" />
          </div>

          {/* File info */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-secondary-700">{value.name}</p>
            <p className="text-xs text-secondary-400">{formatFileSize(value.size)} · Click to change</p>
          </div>

          {/* Remove button (X) */}
          <button
            type="button"
            aria-label="Remove file"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full p-1.5 transition-colors',
              'text-secondary-400 hover:bg-error-50 hover:text-error',
              isDisabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  /* ── Empty / drop-zone state ───────────────────────────────────────────── */

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      {hiddenInput}

      <div
        onClick={openFilePicker}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDisabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={dropZoneCva({ status, isDragOver, isDisabled })}
      >
        <div className={uploadIconBgCva({ site: theme, isDragOver })}>
          <Upload className={cn('h-5 w-5', isDragOver ? 'text-primary-500' : 'text-secondary-400')} />
        </div>

        <div className="text-center">
          <p className="text-sm text-secondary-500">
            <span className="font-semibold text-primary-500 underline">Upload an image </span> or drag and drop
          </p>
          {hint && <p className="mt-1 text-xs">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
